import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, ListBullets, MagicWand } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InlineEdit } from '@/components/DraggableList';
import { PF, AMBITOS } from './pfApi';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const COLORS = ['#657161', '#7A8B76', '#D7BD7B', '#BBD6D5', '#8FA68C', '#A3B89E', '#5B8FC9', '#C97B5B'];

export default function PfCategoriesAccounts({ categories, accounts, settings, reload, onOpenStatement }) {
  const confirmDelete = useConfirmDelete();
  const [catName, setCatName] = useState('');
  const [catType, setCatType] = useState('despesa');
  const [catColor, setCatColor] = useState(COLORS[0]);
  const [catAmbito, setCatAmbito] = useState('');
  const [accName, setAccName] = useState('');
  const [pct, setPct] = useState(settings?.default_duarte_pct ?? 50);
  const [migrateReport, setMigrateReport] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const createCategory = async () => {
    if (!catName.trim()) { toast.error('Nome obrigatório'); return; }
    try { await axios.post(`${PF}/categories`, { name: catName.trim(), type: catType, color: catColor, ambito: catAmbito }); toast.success('Categoria criada'); setCatName(''); reload(); }
    catch { toast.error('Erro ao criar'); }
  };
  const renameCategory = async (id, name) => { await axios.put(`${PF}/categories/${id}`, { name }); reload(); };
  const setBudget = async (id, monthly_budget) => { await axios.put(`${PF}/categories/${id}`, { monthly_budget: parseFloat(monthly_budget) || 0 }); reload(); };
  const delCategory = async (id) => {
    const c = categories.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a categoria "${c?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${PF}/categories/${id}`); toast.success('Eliminada'); reload(); } catch { toast.error('Erro'); }
  };

  const createAccount = async () => {
    if (!accName.trim()) { toast.error('Nome obrigatório'); return; }
    try { await axios.post(`${PF}/accounts`, { name: accName.trim() }); toast.success('Conta criada'); setAccName(''); reload(); }
    catch { toast.error('Erro ao criar'); }
  };
  const renameAccount = async (id, name) => { await axios.put(`${PF}/accounts/${id}`, { name }); reload(); };
  const setInitialBalance = async (id, value) => { await axios.put(`${PF}/accounts/${id}`, { initial_balance: value === '' ? null : value }); reload(); };
  const delAccount = async (id) => {
    const a = accounts.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a conta "${a?.name || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${PF}/accounts/${id}`); toast.success('Eliminada'); reload(); } catch { toast.error('Erro'); }
  };

  const saveSettings = async () => {
    try { await axios.put(`${PF}/settings`, { default_duarte_pct: parseFloat(pct) || 50 }); toast.success('Definições guardadas'); }
    catch { toast.error('Erro'); }
  };

  const setAccountAmbito = async (id, default_ambito) => { await axios.put(`${PF}/accounts/${id}`, { default_ambito }); reload(); };

  const simulateMigration = async () => {
    setMigrating(true);
    try { const { data } = await axios.post(`${PF}/admin/migrate-ambito?dry_run=true`); setMigrateReport(data); }
    catch { toast.error('Erro ao simular'); }
    setMigrating(false);
  };
  const confirmMigration = async () => {
    setMigrating(true);
    try { const { data } = await axios.post(`${PF}/admin/migrate-ambito?dry_run=false`); setMigrateReport({ ...data, saved: true }); toast.success('Migração gravada'); }
    catch { toast.error('Erro ao gravar'); }
    setMigrating(false);
  };

  return (
    <div data-testid="pf-settings-tab" className="space-y-6">
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <p className="text-sm font-medium text-brand-text mb-3">Divisão por defeito com o Duarte</p>
        <div className="flex items-center gap-2">
          <Input type="number" min="0" max="100" value={pct} onChange={e => setPct(e.target.value)} className="rounded-xl border-brand-border w-24" data-testid="pf-settings-duarte-pct" />
          <span className="text-sm text-brand-muted">% para o Duarte</span>
          <button onClick={saveSettings} className="ml-auto bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium" data-testid="pf-settings-save-pct">Guardar</button>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-brand-text mb-2">Categorias</p>
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex gap-1">
              <button onClick={() => setCatType('despesa')} className={`px-3 py-1.5 rounded-full text-xs ${catType === 'despesa' ? 'bg-brand-text text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid="pf-cat-type-despesa">Despesa</button>
              <button onClick={() => setCatType('receita')} className={`px-3 py-1.5 rounded-full text-xs ${catType === 'receita' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`} data-testid="pf-cat-type-receita">Receita</button>
            </div>
            <Input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Nome da categoria" className="rounded-xl border-brand-border flex-1 min-w-[140px]" onKeyDown={e => e.key === 'Enter' && createCategory()} data-testid="pf-cat-name-input" />
            <Select value={catAmbito} onValueChange={setCatAmbito}>
              <SelectTrigger className="rounded-xl border-brand-border w-40" data-testid="pf-cat-ambito-select"><SelectValue placeholder="Âmbito (opcional)" /></SelectTrigger>
              <SelectContent>{AMBITOS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-1">{COLORS.map(c => <button key={c} onClick={() => setCatColor(c)} className={`w-5 h-5 rounded-full border-2 ${catColor === c ? 'border-brand-text' : 'border-transparent'}`} style={{ background: c }} />)}</div>
            <button onClick={createCategory} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm" data-testid="pf-cat-create-btn"><Plus size={14} /></button>
          </div>
        </div>
        <div className="space-y-1.5">
          {categories.map(c => (
            <div key={c.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center gap-3" data-testid={`pf-cat-${c.id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} />
              <div className="flex-1 min-w-0"><InlineEdit value={c.name} onSave={v => renameCategory(c.id, v)} className="text-sm font-medium text-brand-text" /></div>
              {c.ambito && <span className="text-[10px] text-brand-green bg-brand-green/10 px-1.5 py-0.5 rounded-full shrink-0">{c.ambito}</span>}
              <span className="text-[10px] text-brand-muted shrink-0">{c.type}</span>
              {c.type === 'despesa' && (
                <Input defaultValue={c.monthly_budget || ''} onBlur={e => setBudget(c.id, e.target.value)} placeholder="Orçamento €" className="rounded-lg border-brand-border w-24 h-7 text-xs shrink-0" data-testid={`pf-cat-budget-${c.id}`} />
              )}
              <button onClick={() => delCategory(c.id)} className="text-brand-placeholder hover:text-red-500 p-1 shrink-0" data-testid={`pf-cat-delete-${c.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-brand-text mb-2">Contas</p>
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-3">
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label className="text-xs text-brand-muted mb-1 block">Nova conta</Label><Input value={accName} onChange={e => setAccName(e.target.value)} placeholder="Ex: Conta à Ordem" className="rounded-xl border-brand-border" onKeyDown={e => e.key === 'Enter' && createAccount()} data-testid="pf-acc-name-input" /></div>
            <button onClick={createAccount} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm" data-testid="pf-acc-create-btn"><Plus size={14} /></button>
          </div>
        </div>
        <div className="space-y-1.5">
          {accounts.map(a => (
            <div key={a.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center gap-3" data-testid={`pf-acc-${a.id}`}>
              <div className="flex-1"><InlineEdit value={a.name} onSave={v => renameAccount(a.id, v)} className="text-sm font-medium text-brand-text" /></div>
              <Select value={a.default_ambito || ''} onValueChange={v => setAccountAmbito(a.id, v)}>
                <SelectTrigger className="rounded-lg border-brand-border w-32 h-7 text-xs shrink-0" data-testid={`pf-acc-ambito-${a.id}`}><SelectValue placeholder="Âmbito padrão" /></SelectTrigger>
                <SelectContent>{AMBITOS.map(am => <SelectItem key={am} value={am}>{am}</SelectItem>)}</SelectContent>
              </Select>
              <Input defaultValue={a.initial_balance ?? ''} onBlur={e => setInitialBalance(a.id, e.target.value)} placeholder="Saldo inicial €" className="rounded-lg border-brand-border w-28 h-7 text-xs shrink-0" data-testid={`pf-acc-initial-balance-${a.id}`} />
              <button onClick={() => onOpenStatement?.(a.id)} className="text-[11px] text-brand-green flex items-center gap-1 shrink-0" data-testid={`pf-acc-extrato-${a.id}`}><ListBullets size={13} /> Extrato</button>
              <button onClick={() => delAccount(a.id)} className="text-brand-placeholder hover:text-red-500 p-1 shrink-0" data-testid={`pf-acc-delete-${a.id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-brand-text mb-2">Migração de âmbito</p>
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="pf-migration-panel">
          <p className="text-xs text-brand-muted mb-3">Classifica automaticamente o âmbito dos movimentos por regras de texto (descrição + categoria). Corre sempre primeiro em modo simulação, só grava depois de confirmares.</p>
          <div className="flex gap-2 mb-3">
            <button onClick={simulateMigration} disabled={migrating} className="bg-brand-bg text-brand-text rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="pf-migration-simulate-btn"><MagicWand size={14} /> Simular</button>
            {migrateReport && !migrateReport.saved && (
              <button onClick={confirmMigration} disabled={migrating} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium disabled:opacity-50" data-testid="pf-migration-confirm-btn">Confirmar e gravar</button>
            )}
          </div>
          {migrateReport && (
            <div data-testid="pf-migration-report">
              <p className="text-xs font-medium text-brand-text mb-1">{migrateReport.saved ? 'Gravado' : 'Simulação'}, {migrateReport.total_movimentos} movimentos</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {Object.entries(migrateReport.report).map(([ambito, count]) => (
                  <span key={ambito} className="text-[10px] bg-brand-bg text-brand-text px-2 py-1 rounded-full" data-testid={`pf-migration-count-${ambito}`}>{ambito}: {count}</span>
                ))}
              </div>
              {migrateReport.por_classificar?.length > 0 && (
                <div>
                  <p className="text-[11px] text-amber-700 mb-1">Por classificar (sem regra correspondente):</p>
                  {migrateReport.por_classificar.map(m => (
                    <p key={m.id} className="text-[11px] text-brand-muted">{fmtDatePt(m.date)} · {m.note} · {m.amount}€</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
